import { useCallback, useEffect, useRef, useState } from "react";
import { marked } from "marked";
import { useRouter, useParams } from "../lib/router";
import { useAuth } from "../lib/auth-context";
import { DictationEditor, type EditorHandle } from "../components/dictation-editor";
import { MicButton } from "../components/mic-button";
import { AudioUpload } from "../components/audio-upload";
import { ModelLoader } from "../components/model-loader";
import { SOPPreview } from "../components/sop-preview";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { VersionHistory } from "../components/version-history";
import { loadSurveyTemplates, getTemplate, type SurveyTemplate } from "../lib/survey-templates";
import { saveSurvey, getSurvey, type SurveyStatus } from "../lib/surveys";
import { saveVersion, getVersions, type DraftStatus, type DraftVersion } from "../lib/drafts";
import { applyWordReplacements, type WordReplacementMap } from "../lib/word-replacement";
import { loadReplacements } from "../lib/replacement-store";
import { sendMessage, getRoomName, findOrCreateVoxDocRoom, uploadFile, sendFileMessage, type RoomInfo } from "../lib/matrix-client";
import { useOnline } from "../lib/use-online";
import { useTranscriber } from "../lib/use-transcriber";

// Map survey status to draft status for version storage
function toDraftStatus(s: SurveyStatus): DraftStatus {
  if (s === "in-progress") return "draft";
  return s;
}

export default function SurveyPage() {
  const { navigate } = useRouter();
  const { id: surveyId } = useParams();
  const { auth, isLoading: authLoading } = useAuth();
  const isOnline = useOnline();
  const editorRef = useRef<EditorHandle>(null);
  const transcriber = useTranscriber();

  // Template selection (templates loaded from server at mount; falls back to defaults)
  const [templates, setTemplates] = useState<SurveyTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [surveyTitle, setSurveyTitle] = useState("");

  // Editor state
  const [editorText, setEditorText] = useState("");
  const [editorHtml, setEditorHtml] = useState("");
  const [initialContent, setInitialContent] = useState("");

  // Transcription state
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [replacementCount, setReplacementCount] = useState(0);
  const replacementsRef = useRef<WordReplacementMap>({});

  // AI structuring state
  const [isStructuring, setIsStructuring] = useState(false);
  const [structuredOutput, setStructuredOutput] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [showQuestions, setShowQuestions] = useState(true);

  // Publish state
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishStatus, setPublishStatus] = useState("");
  const [targetRoom, setTargetRoom] = useState<RoomInfo | null>(null);
  const [loadingTarget, setLoadingTarget] = useState(false);
  const [targetError, setTargetError] = useState<string | null>(null);

  // Draft lifecycle state
  const [surveyStatus, setSurveyStatus] = useState<SurveyStatus>("in-progress");
  const [rawDictation, setRawDictation] = useState("");
  const [markdownSource, setMarkdownSource] = useState("");
  const [copied, setCopied] = useState(false);
  const [saveConfirm, setSaveConfirm] = useState(false);
  const [versions, setVersions] = useState<DraftVersion[]>([]);

  // Save timer
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs for latest state (used by unmount save)
  const latestRef = useRef({
    selectedTemplate, surveyTitle, editorHtml, editorText, surveyStatus,
    rawDictation, markdownSource, structuredOutput, surveyId,
  });
  latestRef.current = {
    selectedTemplate, surveyTitle, editorHtml, editorText, surveyStatus,
    rawDictation, markdownSource, structuredOutput, surveyId,
  };

  // Load word replacements
  useEffect(() => {
    loadReplacements().then(({ map }) => {
      replacementsRef.current = map;
    });
  }, []);

  // Load survey templates from API (with bundled defaults as fallback)
  useEffect(() => {
    loadSurveyTemplates().then(setTemplates);
  }, []);

  // Load existing survey
  useEffect(() => {
    getSurvey(surveyId).then((survey) => {
      if (survey) {
        setSelectedTemplate(survey.templateId);
        setSurveyTitle(survey.title);
        setSurveyStatus(survey.status);
        if (survey.structuredOutput) setStructuredOutput(survey.structuredOutput);
        if (survey.rawDictation) setRawDictation(survey.rawDictation);
        if (survey.markdownSource) {
          setMarkdownSource(survey.markdownSource);
          setStructuredOutput(survey.markdownSource);
          const html = marked.parse(survey.markdownSource) as string;
          setInitialContent(html);
        } else {
          // Restore dictation text from answers
          const freeformAnswer = survey.answers.find((a) => a.questionIndex === -1);
          if (freeformAnswer?.answer) {
            setEditorText(freeformAnswer.answer);
            setInitialContent(freeformAnswer.answer);
          }
        }
      }
    });
    getVersions(surveyId).then(setVersions);
  }, [surveyId]);

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !auth) navigate("/login");
  }, [auth, authLoading, navigate]);

  // Wire up transcriber results
  useEffect(() => {
    transcriber.onResult((text: string) => {
      const { text: replaced, count } = applyWordReplacements(text, replacementsRef.current);
      setReplacementCount(count);
      editorRef.current?.appendText(replaced);
      setIsTranscribing(false);
    });
  }, [transcriber]);

  // Auto-load model for surveys (users expect dictation immediately)
  useEffect(() => {
    if (transcriber.modelStatus === "idle") {
      transcriber.loadModel();
    }
  }, [transcriber]);

  const doSave = useCallback(() => {
    const s = latestRef.current;
    if (!s.selectedTemplate) return;
    const template = getTemplate(templates, s.selectedTemplate);
    if (!template) return;
    saveSurvey({
      id: s.surveyId,
      templateId: s.selectedTemplate,
      templateName: template.name,
      title: s.surveyTitle || `${template.name} Survey`,
      answers: [{ questionIndex: -1, question: "Freeform dictation", answer: s.editorText }],
      updatedAt: Date.now(),
      status: s.surveyStatus,
      structuredOutput: s.structuredOutput || undefined,
      rawDictation: s.rawDictation || undefined,
      markdownSource: s.markdownSource || undefined,
    });
  }, [surveyId]);

  // Auto-save with debounce
  const handleEditorUpdate = useCallback((html: string, text: string) => {
    setEditorHtml(html);
    setEditorText(text);
    latestRef.current.editorHtml = html;
    latestRef.current.editorText = text;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(doSave, 2000);
  }, [doSave]);

  // Save on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      doSave();
    };
  }, [doSave]);

  const handleAudioReady = useCallback((audioData: Float32Array) => {
    setIsTranscribing(true);
    transcriber.transcribe(audioData);
  }, [transcriber]);

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = getTemplate(templates, templateId);
    if (template) setSurveyTitle(`${template.name} Survey`);
  };

  const handleTitleChange = (newTitle: string) => {
    setSurveyTitle(newTitle);
    doSave();
  };

  const refreshVersions = async () => {
    const v = await getVersions(surveyId);
    setVersions(v);
  };

  const loadRooms = () => {
    if (!auth || loadingTarget || targetRoom) return;
    setLoadingTarget(true);
    setTargetError(null);
    findOrCreateVoxDocRoom(auth)
      .then((room) => setTargetRoom(room))
      .catch((err) => {
        console.error("[VoxDoc] failed to resolve target room:", err);
        setTargetError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoadingTarget(false));
  };

  const handleCheckCoverage = async () => {
    const template = selectedTemplate ? getTemplate(templates, selectedTemplate) : null;
    if (!template || !auth || !editorText.trim()) return;
    setIsStructuring(true);

    // Save raw dictation and version before AI processing
    setRawDictation(editorText);
    await saveVersion(surveyId, editorText, "Before AI processing", toDraftStatus(surveyStatus), markdownSource || undefined);
    refreshVersions();

    try {
      const res = await fetch("/api/structure", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: editorText,
          author: auth.userId?.replace(/^@/, "").replace(/:.*$/, ""),
          surveyType: template.name,
          surveyQuestions: template.questions,
        }),
      });
      if (!res.ok) throw new Error("AI processing failed");
      const data = await res.json();
      setStructuredOutput(data.markdown);
      setMarkdownSource(data.markdown);
      setShowPreview(true);
      loadRooms();
    } catch (err) {
      console.error(err);
    } finally {
      setIsStructuring(false);
    }
  };

  const applyStructuredToEditor = (markdown: string) => {
    const html = marked.parse(markdown) as string;
    editorRef.current?.replaceContent(html);
    setEditorText(markdown);
    setEditorHtml(html);
    setSurveyStatus("structured");
    doSave();
  };

  const handleUseStructured = () => {
    if (structuredOutput) {
      applyStructuredToEditor(structuredOutput);
    }
    setShowPreview(false);
  };

  const handleRevertToRaw = () => {
    if (!rawDictation) return;
    editorRef.current?.replaceContent(rawDictation);
    setEditorText(rawDictation);
    setEditorHtml(rawDictation);
    setMarkdownSource("");
    setStructuredOutput("");
    setSurveyStatus("in-progress");
    doSave();
  };

  const handleCopyMarkdown = async () => {
    const textToCopy = markdownSource || editorText;
    await navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePublish = async (roomId: string) => {
    if (!auth || !structuredOutput || !roomId) return;
    setIsPublishing(true);
    await saveVersion(surveyId, editorText, "Before publishing", toDraftStatus(surveyStatus), markdownSource || undefined);
    refreshVersions();
    try {
      const roomName = await getRoomName(auth, roomId);
      const docTitle = surveyTitle || "Untitled Survey";
      const filename = `${docTitle.replace(/[^a-zA-Z0-9-_ ]/g, "").replace(/\s+/g, "-")}.md`;

      const header = `\u{1F4CB} **${docTitle}**\n\n`;
      await sendMessage(auth, roomId, header + structuredOutput);

      try {
        const blob = new Blob([structuredOutput], { type: "text/markdown" });
        const mxcUri = await uploadFile(auth, blob, filename, "text/markdown");
        await sendFileMessage(auth, roomId, mxcUri, filename, blob.size, "text/markdown");
      } catch (fileErr) {
        console.warn("File attachment failed (survey text was still sent):", fileErr);
      }

      applyStructuredToEditor(structuredOutput);
      setSurveyStatus("published");
      doSave();
      setShowPreview(false);
      setPublishStatus(`Published to: ${roomName} (${filename})`);
    } catch (err) {
      console.error(err);
      setPublishStatus(
        `Publish failed: ${err instanceof Error ? err.message : "Unknown error"}`
      );
    } finally {
      setIsPublishing(false);
    }
  };

  const handleBack = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    doSave();
    navigate("/");
  };

  const template = selectedTemplate ? getTemplate(templates, selectedTemplate) : null;

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  // Template selection screen
  if (!selectedTemplate) {
    return (
      <div className="min-h-screen p-3 sm:p-6 max-w-2xl mx-auto">
        <header className="flex items-center justify-between mb-6">
          <Button variant="ghost" onClick={() => navigate("/")}>
            &larr; Back
          </Button>
          <h1 className="text-xl font-bold">New Survey</h1>
          <div />
        </header>

        <p className="text-muted-foreground mb-4">
          Select a system type to start your site survey:
        </p>

        <div className="grid gap-3">
          {templates.length === 0 && (
            <p className="text-sm text-muted-foreground">Loading templates…</p>
          )}
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => handleSelectTemplate(t.id)}
              className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-primary hover:bg-muted/50 transition-colors text-left"
            >
              <div>
                <p className="font-medium">{t.name}</p>
                <p className="text-sm text-muted-foreground">
                  {t.questions.length} question{t.questions.length !== 1 ? "s" : ""}
                </p>
              </div>
              <span className="text-muted-foreground">&rarr;</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const statusColors: Record<SurveyStatus, string> = {
    "in-progress": "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
    structured: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    published: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  };

  const statusLabel = surveyStatus === "in-progress" ? "In Progress" : surveyStatus.charAt(0).toUpperCase() + surveyStatus.slice(1);

  // Freeform dictation survey with full workflow
  return (
    <div className="min-h-screen p-3 sm:p-6 max-w-3xl mx-auto">
      <header className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={handleBack}>
          &larr; Back
        </Button>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{template?.name}</Badge>
          <Badge className={statusColors[surveyStatus]}>
            {statusLabel}
          </Badge>
          {replacementCount > 0 && (
            <Badge variant="outline" className="text-emerald-500">
              {replacementCount} replacement{replacementCount > 1 ? "s" : ""}
            </Badge>
          )}
        </div>
      </header>

      <Input
        value={surveyTitle}
        onChange={(e) => handleTitleChange(e.target.value)}
        placeholder="Survey Title"
        className="text-xl font-semibold mb-4 border-none bg-transparent p-0 focus-visible:ring-0"
      />

      <ModelLoader status={transcriber.modelStatus} progress={transcriber.loadProgress} error={transcriber.error || ""} isIOS={transcriber.isIOS} />

      {/* Question reference panel */}
      <div className="mb-4">
        <button
          onClick={() => setShowQuestions(!showQuestions)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <span>{showQuestions ? "\u25BC" : "\u25B6"}</span>
          <span>Survey Questions ({template?.questions.length})</span>
        </button>
        {showQuestions && template && (
          <div className="mt-2 p-3 rounded-lg border border-border bg-muted/30 max-h-[200px] overflow-y-auto">
            <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
              {template.questions.map((q, i) => (
                <li key={i}>{q}</li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {/* Dictation controls */}
      <div className="my-4 flex flex-wrap items-center gap-4">
        <MicButton
          onAudioReady={handleAudioReady}
          disabled={transcriber.modelStatus !== "ready" || isTranscribing}
        />
        <AudioUpload
          onAudioReady={handleAudioReady}
          disabled={transcriber.modelStatus !== "ready"}
        />
        {isTranscribing && (
          <span className="text-sm text-purple-400 animate-pulse">Transcribing...</span>
        )}
      </div>

      <p className="text-xs text-muted-foreground mb-2">
        Dictate freely — describe what you see on site. When done, hit "Check Coverage" to process with AI.
      </p>

      {/* Freeform editor */}
      <DictationEditor
        ref={editorRef}
        content={initialContent}
        onUpdate={handleEditorUpdate}
      />

      {/* Action buttons */}
      <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <Button
          onClick={handleCheckCoverage}
          disabled={!editorText.trim() || isStructuring || !isOnline}
        >
          {!isOnline ? "Offline" : isStructuring ? "Processing..." : surveyStatus === "structured" ? "Re-check Coverage" : "Check Coverage"}
        </Button>

        {rawDictation && surveyStatus !== "in-progress" && (
          <Button variant="outline" onClick={handleRevertToRaw}>
            Revert to Raw
          </Button>
        )}

        <Button variant="outline" onClick={handleCopyMarkdown}>
          {copied ? "Copied!" : "Copy Markdown"}
        </Button>

        {(editorText.trim() || editorHtml.trim()) && (
          <Button variant="outline" onClick={async () => {
            const content = editorRef.current?.getText() || editorText;
            await saveVersion(surveyId, content, "Manual save", toDraftStatus(surveyStatus), markdownSource || undefined);
            await refreshVersions();
            setSaveConfirm(true);
            setTimeout(() => setSaveConfirm(false), 2000);
          }}>
            {saveConfirm ? "Saved!" : "Save Version"}
          </Button>
        )}

        {structuredOutput && !showPreview && surveyStatus !== "in-progress" && (
          <Button variant="outline" onClick={() => { setShowPreview(true); loadRooms(); }}>
            Preview & Publish
          </Button>
        )}
      </div>

      <VersionHistory
        versions={versions}
        onRestore={(v) => {
          if (v.markdownSource) {
            const html = marked.parse(v.markdownSource) as string;
            editorRef.current?.replaceContent(html);
            setEditorText(v.content);
            setEditorHtml(html);
            setMarkdownSource(v.markdownSource);
            setStructuredOutput(v.markdownSource);
          } else {
            editorRef.current?.replaceContent(v.content);
            setEditorText(v.content);
            setEditorHtml(v.content);
            setMarkdownSource("");
            setStructuredOutput("");
          }
          const newStatus = v.status === "draft" ? "in-progress" as SurveyStatus : v.status as SurveyStatus;
          setSurveyStatus(newStatus);
          doSave();
        }}
      />

      {publishStatus && (
        <div className={`mt-4 p-3 rounded-lg text-sm ${
          publishStatus.startsWith("Published")
            ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
            : "bg-destructive/10 text-destructive border border-destructive/20"
        }`}>
          {publishStatus}
        </div>
      )}

      <SOPPreview
        open={showPreview}
        onClose={handleUseStructured}
        markdown={structuredOutput}
        onPublish={handlePublish}
        publishing={isPublishing}
        targetRoom={targetRoom}
        loadingTarget={loadingTarget}
        targetError={targetError}
        title="Structured Survey Preview"
      />
    </div>
  );
}
